/**
 * storefrontPreviewFixtures.js — DEMO catalog for the admin Design Studio live preview.
 *
 * ⚠️  PREVIEW-ONLY DATA. These fixtures render EXCLUSIVELY when the unified
 * storefront (MealPassProgramStore) is mounted with `?preview=1` — i.e. inside
 * the admin Design Studio iframe — AND the corresponding real catalog fetch
 * failed (e.g. 403 while the storefront is disabled) or came back empty.
 * They are NEVER shown to real customers: every substitution site in
 * MealPassProgramStore.fetchAll is guarded by `isPreview`.
 *
 * Shapes mirror the real API responses the component consumes:
 *   - DEMO_PROGRAMS       → GET /api/meal-pass/public/{subdomain}/programs  (data.programs[])
 *   - DEMO_MEAL_PLANS     → GET /api/storefront/meal-plans/{subdomain}      (mpj.meal_plans[])
 *   - DEMO_TAGS           → GET /api/storefront/tags/{subdomain}            (tj.tags[])
 *   - DEMO_MEAL_ITEMS     → GET /api/storefront/meal-items/{subdomain}      (mij.meal_items[])
 *   - DEMO_MENU_CALENDAR  → GET /api/storefront/menu-calendar/{subdomain}   (mcj.entries[])
 *   - DEMO_CATERING_PACKAGES / DEMO_CATERING_AVAILABILITY
 *                         → GET /api/catering/public/{subdomain}/packages   (cj.packages[] / cj.availability)
 *
 * Keep field names in sync with the render code in MealPassProgramStore.jsx —
 * these exist so the designer sees the storefront exactly as customers would.
 */

// Local-date ISO helper (yyyy-mm-dd). Computed at import time so the demo menu
// calendar always spans today .. today+4 — matching the real rolling window.
const isoDaysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ── Meal pass programs ────────────────────────────────────────────────────
// Fields read by the programs section: id, name, public_name, description,
// credit_count, price, currency, savings_label, allowed_fulfillment[], image_url.
export const DEMO_PROGRAMS = [
  {
    id: 'demo-pass-starter',
    name: 'Starter Pass',
    public_name: 'The Starter',
    description:
      'Five meals, zero commitment. Perfect for trying us out — redeem for any regular menu item whenever the craving hits.',
    credit_count: 5,
    price: 79.0,
    currency: 'CAD',
    savings_label: 'Save 8% vs paying per meal',
    allowed_fulfillment: ['dine_in', 'pickup'],
    image_url:
      'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1200&q=60',
  },
  {
    id: 'demo-pass-regular',
    name: 'Regulars Pass',
    public_name: 'The Regular',
    description:
      'Ten meals for the people who already know what they like. Our most popular bundle — lunch is covered for two weeks straight.',
    credit_count: 10,
    price: 145.0,
    currency: 'CAD',
    savings_label: 'Save 15% — most popular',
    allowed_fulfillment: ['dine_in', 'pickup', 'delivery'],
    image_url:
      'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1200&q=60',
  },
  {
    id: 'demo-pass-devotee',
    name: 'Devotee Pass',
    public_name: 'The Devotee',
    description:
      'Twenty meals at our very best price. For the desk-lunch loyalists, the family meal planners, and everyone in between.',
    credit_count: 20,
    price: 260.0,
    currency: 'CAD',
    savings_label: 'Best value — save 24%',
    allowed_fulfillment: ['dine_in', 'pickup', 'delivery'],
    image_url:
      'https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1200&q=60',
  },
];

// ── Meal plans (subscriptions) ────────────────────────────────────────────
// Fields read by the meal-plans section + AddToCartModal/AddonsOfferModal:
// id, name, description, is_active, is_meal_addon, is_delivery_addon,
// images[] (first is card cover) / image_url, pricing.tiers[].{quantity,price},
// store_ids[], tag_ids[]. The last entry is an add-on (upsell modal), so it is
// intentionally excluded from the primary cards by the existing filter.
export const DEMO_MEAL_PLANS = [
  {
    id: 'demo-plan-weekday-lunch',
    name: 'Weekday Lunch Box',
    description:
      'A chef-planned lunch delivered Monday to Friday. Rotating mains, seasonal sides, and no 11:45am panic about what to eat.',
    is_active: true,
    is_meal_addon: false,
    is_delivery_addon: false,
    images: [
      'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1200&q=60',
    ],
    pricing: { tiers: [{ quantity: 5, price: 74.5 }, { quantity: 10, price: 139.0 }] },
    store_ids: [],
    tag_ids: ['demo-tag-lunch'],
  },
  {
    id: 'demo-plan-family-dinner',
    name: 'Family Dinner Plan',
    description:
      'Generous family-style dinners, four nights a week. Built for sharing — mains, sides, and something sweet to finish.',
    is_active: true,
    is_meal_addon: false,
    is_delivery_addon: false,
    images: [
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=60',
    ],
    pricing: { tiers: [{ quantity: 4, price: 96.0 }, { quantity: 8, price: 184.0 }] },
    store_ids: [],
    tag_ids: ['demo-tag-family'],
  },
  {
    id: 'demo-plan-plant-power',
    name: 'Plant Power Week',
    description:
      'Seven fully plant-based meals — bright bowls, hearty curries, and grain plates that never feel like a compromise.',
    is_active: true,
    is_meal_addon: false,
    is_delivery_addon: false,
    images: [
      'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1200&q=60',
    ],
    pricing: { tiers: [{ quantity: 7, price: 89.0 }] },
    store_ids: [],
    tag_ids: ['demo-tag-veg'],
  },
  {
    id: 'demo-plan-addon-dessert',
    name: 'Dessert of the Day',
    description: 'Add the kitchen’s daily dessert to every delivery.',
    is_active: true,
    is_meal_addon: true,
    is_delivery_addon: false,
    image_url:
      'https://images.unsplash.com/photo-1551024506-0bccd828d307?auto=format&fit=crop&w=800&q=60',
    pricing: { tiers: [{ quantity: 1, price: 4.5 }] },
    store_ids: [],
    tag_ids: [],
  },
];

// ── Tags (meal-plan filter chips) ─────────────────────────────────────────
// Fields read by the tag filter: id, name, display_name, icon,
// meal_plan_count, meal_plan_ids[] (must reference DEMO_MEAL_PLANS ids so the
// chips actually filter the demo cards in the preview).
export const DEMO_TAGS = [
  {
    id: 'demo-tag-lunch',
    name: 'lunch',
    display_name: 'Lunch',
    icon: '🥪',
    meal_plan_count: 1,
    meal_plan_ids: ['demo-plan-weekday-lunch'],
  },
  {
    id: 'demo-tag-family',
    name: 'family',
    display_name: 'Family Style',
    icon: '🍽️',
    meal_plan_count: 1,
    meal_plan_ids: ['demo-plan-family-dinner'],
  },
  {
    id: 'demo-tag-veg',
    name: 'vegetarian',
    display_name: 'Vegetarian',
    icon: '🌱',
    meal_plan_count: 1,
    meal_plan_ids: ['demo-plan-plant-power'],
  },
];

// ── À-la-carte meal items ─────────────────────────────────────────────────
// Fields read by the category-mode menu: id, name, description, category,
// price (falls back to base_price / price_tiers[0].price), image_url, is_active.
export const DEMO_MEAL_ITEMS = [
  {
    id: 'demo-item-butter-chicken',
    name: 'Butter Chicken Bowl',
    description: 'Slow-simmered tomato-butter gravy, charred chicken, basmati rice, house pickles.',
    category: 'Mains',
    price: 15.5,
    is_active: true,
    image_url:
      'https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-item-miso-salmon',
    name: 'Miso-Glazed Salmon',
    description: 'Pan-seared salmon, ginger-miso glaze, sesame greens, and steamed jasmine rice.',
    category: 'Mains',
    price: 18.0,
    is_active: true,
    image_url:
      'https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-item-paneer-tikka',
    name: 'Paneer Tikka Plate',
    description: 'Tandoor-charred paneer, mint chutney, warm naan, and a crisp kachumber salad.',
    category: 'Mains',
    price: 14.0,
    is_active: true,
    image_url:
      'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-item-harvest-bowl',
    name: 'Harvest Grain Bowl',
    description: 'Roasted squash, quinoa, kale, toasted seeds, and a maple-tahini dressing.',
    category: 'Bowls & Salads',
    price: 12.5,
    is_active: true,
    image_url:
      'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-item-thai-noodle-salad',
    name: 'Thai Noodle Salad',
    description: 'Rice noodles, crunchy veg, herbs, crushed peanuts, and a lime-chili dressing.',
    category: 'Bowls & Salads',
    price: 11.75,
    is_active: true,
    image_url:
      'https://images.unsplash.com/photo-1547592166-23ac45744acd?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-item-chocolate-torte',
    name: 'Flourless Chocolate Torte',
    description: 'Dense, dark, and just bitter enough — with a whisper of sea salt on top.',
    category: 'Desserts',
    price: 7.5,
    is_active: true,
    image_url:
      'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=800&q=60',
  },
];

// ── Menu calendar (date-mode menu) ────────────────────────────────────────
// Fields read by the date-mode menu: id, date (yyyy-mm-dd), name, description,
// category, price, image_url, variant. Two entries per day, today .. today+4,
// so the per-day sections of the preview always have content.
export const DEMO_MENU_CALENDAR = [
  {
    id: 'demo-cal-d0-a',
    date: isoDaysFromNow(0),
    name: 'Butter Chicken Bowl',
    description: 'Slow-simmered tomato-butter gravy, charred chicken, basmati rice.',
    category: 'Mains',
    price: 15.5,
    variant: 'Regular · 550 cal',
    image_url:
      'https://images.unsplash.com/photo-1585937421612-70a008356fbe?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d0-b',
    date: isoDaysFromNow(0),
    name: 'Harvest Grain Bowl',
    description: 'Roasted squash, quinoa, kale, toasted seeds, maple-tahini dressing.',
    category: 'Bowls & Salads',
    price: 12.5,
    variant: 'Vegan',
    image_url:
      'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d1-a',
    date: isoDaysFromNow(1),
    name: 'Miso-Glazed Salmon',
    description: 'Pan-seared salmon, ginger-miso glaze, sesame greens, jasmine rice.',
    category: 'Mains',
    price: 18.0,
    variant: 'Gluten-free option',
    image_url:
      'https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d1-b',
    date: isoDaysFromNow(1),
    name: 'Thai Noodle Salad',
    description: 'Rice noodles, crunchy veg, herbs, peanuts, lime-chili dressing.',
    category: 'Bowls & Salads',
    price: 11.75,
    variant: 'Contains peanuts',
    image_url:
      'https://images.unsplash.com/photo-1547592166-23ac45744acd?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d2-a',
    date: isoDaysFromNow(2),
    name: 'Paneer Tikka Plate',
    description: 'Tandoor-charred paneer, mint chutney, warm naan, kachumber salad.',
    category: 'Mains',
    price: 14.0,
    variant: 'Vegetarian',
    image_url:
      'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d2-b',
    date: isoDaysFromNow(2),
    name: 'Roast Chicken & Greens',
    description: 'Herb-roasted half chicken, charred broccolini, lemon jus.',
    category: 'Mains',
    price: 16.25,
    variant: 'Regular · 620 cal',
    image_url:
      'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d3-a',
    date: isoDaysFromNow(3),
    name: 'Wild Mushroom Risotto',
    description: 'Creamy arborio rice, roasted wild mushrooms, parmesan, chives.',
    category: 'Mains',
    price: 14.75,
    variant: 'Vegetarian',
    image_url:
      'https://images.unsplash.com/photo-1476124369491-e7addf5db371?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d3-b',
    date: isoDaysFromNow(3),
    name: 'Falafel Mezze Box',
    description: 'Crisp falafel, hummus, tabbouleh, pickled turnip, warm pita.',
    category: 'Bowls & Salads',
    price: 12.0,
    variant: 'Vegan',
    image_url:
      'https://images.unsplash.com/photo-1540914124281-342587941389?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d4-a',
    date: isoDaysFromNow(4),
    name: 'Braised Beef Short Rib',
    description: 'Red-wine braised short rib, buttery mash, glazed carrots.',
    category: 'Mains',
    price: 19.5,
    variant: 'Regular · 720 cal',
    image_url:
      'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=800&q=60',
  },
  {
    id: 'demo-cal-d4-b',
    date: isoDaysFromNow(4),
    name: 'Flourless Chocolate Torte',
    description: 'Dense, dark, and just bitter enough — sea salt on top.',
    category: 'Desserts',
    price: 7.5,
    variant: 'Gluten-free',
    image_url:
      'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=800&q=60',
  },
];

// ── Catering packages (v4.24) ─────────────────────────────────────────────
// Fields read by the catering section + CateringOrderModal: id, name,
// description, image_url, pricing_model, min_persons, price_per_person,
// included_items[], slots[].{id,name,choose,item_ids,item_names},
// tray_items[].{item_id,name,sizes[].{size,label,serves,price}},
// premium_items[].{id,name,description,pricing_mode,price}.
export const DEMO_CATERING_PACKAGES = [
  {
    id: 'demo-cater-classic',
    name: 'The Classic Spread',
    description:
      'Our most-loved package for office lunches and family gatherings — pick your mains and starters, we handle the rest.',
    image_url:
      'https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&w=1200&q=60',
    pricing_model: 'per_person',
    min_persons: 15,
    price_per_person: 21.5,
    included_items: [
      { name: 'Assorted Bread Basket', description: 'Naan, focaccia, and dinner rolls' },
      { name: 'Seasonal Salad', description: null },
    ],
    slots: [
      {
        id: 'demo-slot-mains',
        name: 'Mains',
        choose: 3,
        item_ids: ['demo-item-butter-chicken', 'demo-item-miso-salmon', 'demo-item-paneer-tikka', 'demo-item-harvest-bowl'],
        item_names: {
          'demo-item-butter-chicken': 'Butter Chicken',
          'demo-item-miso-salmon': 'Miso-Glazed Salmon',
          'demo-item-paneer-tikka': 'Paneer Tikka',
          'demo-item-harvest-bowl': 'Harvest Grain Bowl',
        },
      },
      {
        id: 'demo-slot-apps',
        name: 'Appetizers',
        choose: 2,
        item_ids: ['demo-item-thai-noodle-salad', 'demo-item-falafel', 'demo-item-samosa'],
        item_names: {
          'demo-item-thai-noodle-salad': 'Thai Noodle Salad',
          'demo-item-falafel': 'Crispy Falafel Bites',
          'demo-item-samosa': 'Vegetable Samosas',
        },
      },
    ],
    tray_items: [],
    premium_items: [
      { id: 'demo-prem-dessert', name: 'Dessert Bar', description: 'Torte, gulab jamun & fruit', pricing_mode: 'per_person', price: 4.5 },
      { id: 'demo-prem-setup', name: 'On-site Setup & Chafing Dishes', description: null, pricing_mode: 'flat', price: 75.0 },
    ],
    store_ids: [],
  },
  {
    id: 'demo-cater-trays',
    name: 'Build-Your-Own Trays',
    description:
      'Modern tray catering — pick exactly the dishes and sizes you need. Small feeds 10, medium 20, large 35.',
    image_url:
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=60',
    pricing_model: 'tray',
    min_persons: 10,
    price_per_person: 0,
    included_items: [],
    slots: [],
    tray_items: [
      {
        item_id: 'demo-item-butter-chicken',
        name: 'Butter Chicken',
        sizes: [
          { size: 'S', label: 'Small — serves 10', serves: 10, price: 95.0 },
          { size: 'M', label: 'Medium — serves 20', serves: 20, price: 175.0 },
          { size: 'L', label: 'Large — serves 35', serves: 35, price: 290.0 },
        ],
      },
      {
        item_id: 'demo-item-harvest-bowl',
        name: 'Harvest Grain Salad',
        sizes: [
          { size: 'S', label: 'Small — serves 10', serves: 10, price: 65.0 },
          { size: 'L', label: 'Large — serves 35', serves: 35, price: 195.0 },
        ],
      },
    ],
    premium_items: [
      { id: 'demo-prem-delivery-setup', name: 'White-Glove Delivery & Setup', description: null, pricing_mode: 'flat', price: 60.0 },
    ],
    store_ids: [],
  },
];

// Availability block served alongside the packages — the modal validates the
// event slot client-side with this (server re-enforces at checkout).
export const DEMO_CATERING_AVAILABILITY = {
  min_lead_hours: 48,
  event_start_hour: 8,
  event_end_hour: 21,
  timezone: 'America/Toronto',
  skip_dates: [],
};
